import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from './../axiosInstance.js';
import { Button, Loader, Modal, Text } from '@mantine/core';

const PLATFORM_META = {
  imdb: { label: 'IMDb', icon: '/imdb_small_icon.png' },
  kinopoisk: { label: 'Kinopoisk', icon: '/kinopoisk_small_icon.png' },
  rottenTomatoes: { label: 'Rotten Tomatoes (critics)', icon: '/rottentomatoes_icon.png' },
  rottenTomatoesAudience: { label: 'Rotten Tomatoes (audience)', icon: '/rottentomatoes_icon.png' },
  amazon: { label: 'Amazon', icon: '/amazon_icon.png' },
  goodreads: { label: 'Goodreads', icon: '/goodreads_icon.png' },
  reddit: { label: 'Reddit', icon: '/reddit_icon.png' },
  appleBooks: { label: 'Apple Books', icon: '/applebooks_icon.png' },
  steam: { label: 'Steam', icon: '/steam_icon.png' },
  metacritic: { label: 'Metacritic (critics)', icon: '/metacritic_icon.png' },
  metacriticUser: { label: 'Metacritic (users)', icon: '/metacritic_icon.png' },
  livelib: { label: 'Livelib', icon: '/livelib_icon.png' },
  litres: { label: 'Litres', icon: '/litres_icon.png' },
};

const fmtVotes = (n) => {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'k';
  return String(n);
};

// Inline SVG line chart. Kept dep-free — admin-only surface, one bundle less.
// Rating (blue, solid, left axis) and votes (orange, dashed, right axis)
// share the time x-axis. Votes = -1 is the backend's "unknown" sentinel
// (e.g. Reddit) — those points are dropped from the votes series.
const RatingChart = ({ points, label, icon }) => {
  if (!points || points.length === 0) return null;
  const W = 400;
  const H = 120;
  const padL = 34;
  const padR = 42;
  const padT = 10;
  const padB = 22;
  const minT = points[0].t;
  const maxT = points[points.length - 1].t;
  const dt = Math.max(1, maxT - minT);

  const ratings = points.map((p) => p.v);
  const rMin = Math.min(...ratings);
  const rMax = Math.max(...ratings);
  const rPad = rMax === rMin ? Math.max(0.5, rMax * 0.05) : (rMax - rMin) * 0.1;
  const rMinV = rMin - rPad;
  const rMaxV = rMax + rPad;
  const rDV = Math.max(0.01, rMaxV - rMinV);

  const votesPoints = points.filter((p) => p.votes >= 0);
  const hasVotes = votesPoints.length > 0;
  const voteVals = votesPoints.map((p) => p.votes);
  const vMin = hasVotes ? Math.min(...voteVals) : 0;
  const vMax = hasVotes ? Math.max(...voteVals) : 0;
  const vPad = vMax === vMin ? Math.max(1, vMax * 0.05) : (vMax - vMin) * 0.1;
  const vMinV = vMin - vPad;
  const vMaxV = vMax + vPad;
  const vDV = Math.max(1, vMaxV - vMinV);

  const x = (t) => padL + ((t - minT) / dt) * (W - padL - padR);
  const yR = (v) => H - padB - ((v - rMinV) / rDV) * (H - padT - padB);
  const yV = (v) => H - padB - ((v - vMinV) / vDV) * (H - padT - padB);

  const ratingPath = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${yR(p.v).toFixed(1)}`).join(' ');
  const votesPath = hasVotes
    ? votesPoints.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${yV(p.votes).toFixed(1)}`).join(' ')
    : '';

  const fmtDate = (ms) => new Date(ms).toISOString().slice(0, 10);
  return (
    <div className="my-2">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {icon && <img src={icon} alt={label} className="w-4 h-4 rounded-sm" />}
        <span className="text-xs">{label}</span>
        <span className="text-xs" style={{ color: '#1971c2' }}>
          rating {rMin.toFixed(2)} → {rMax.toFixed(2)}
        </span>
        {hasVotes && (
          <span className="text-xs" style={{ color: '#e8590c' }}>
            votes {fmtVotes(vMin)} → {fmtVotes(vMax)}
          </span>
        )}
      </div>
      <svg width={W} height={H} style={{ background: 'var(--mantine-color-body)', border: '1px solid var(--mantine-color-default-border)', borderRadius: 4 }}>
        <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#ccc" strokeWidth="1" />
        <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#ccc" strokeWidth="1" />
        {hasVotes && <line x1={W - padR} y1={padT} x2={W - padR} y2={H - padB} stroke="#ccc" strokeWidth="1" />}
        <text x={padL - 4} y={yR(rMax) + 3} textAnchor="end" fontSize="9" fill="#1971c2">{rMax.toFixed(2)}</text>
        <text x={padL - 4} y={yR(rMin) + 3} textAnchor="end" fontSize="9" fill="#1971c2">{rMin.toFixed(2)}</text>
        {hasVotes && (
          <>
            <text x={W - padR + 4} y={yV(vMax) + 3} textAnchor="start" fontSize="9" fill="#e8590c">{fmtVotes(vMax)}</text>
            <text x={W - padR + 4} y={yV(vMin) + 3} textAnchor="start" fontSize="9" fill="#e8590c">{fmtVotes(vMin)}</text>
          </>
        )}
        <text x={padL} y={H - 6} textAnchor="start" fontSize="9" fill="#888">{fmtDate(minT)}</text>
        <text x={W - padR} y={H - 6} textAnchor="end" fontSize="9" fill="#888">{fmtDate(maxT)}</text>
        {hasVotes && <path d={votesPath} fill="none" stroke="#e8590c" strokeWidth="1.5" strokeDasharray="3,2" />}
        <path d={ratingPath} fill="none" stroke="#1971c2" strokeWidth="1.5" />
        {points.map((p, i) => (
          <circle key={`r${i}`} cx={x(p.t)} cy={yR(p.v)} r="2" fill="#1971c2">
            <title>{`${fmtDate(p.t)}: ${p.v} (${p.votes >= 0 ? p.votes + ' votes' : 'votes unknown'})`}</title>
          </circle>
        ))}
        {hasVotes && votesPoints.map((p, i) => (
          <circle key={`v${i}`} cx={x(p.t)} cy={yV(p.votes)} r="2" fill="#e8590c">
            <title>{`${fmtDate(p.t)}: ${fmtVotes(p.votes)} votes`}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
};

// AdminRatingHistoryButton renders a button that opens a modal with one
// line chart per platform for the artRatingHistory rows the daily
// UpdateMoviesRatings job snapshots.
const AdminRatingHistoryButton = ({ artId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || loaded || loading) return;
    setLoading(true);
    setError(null);
    axiosInstance
      .get(`/admin/arts/${artId}/ratingHistory`)
      .then((res) => {
        setHistory(res.data.history || []);
        setLoaded(true);
      })
      .catch((e) => setError(e.response?.data || e.message))
      .finally(() => setLoading(false));
  }, [open, artId, loaded, loading]);

  const byPlatform = useMemo(() => {
    const m = {};
    history.forEach((e) => {
      if (!m[e.platform]) m[e.platform] = [];
      m[e.platform].push({ t: new Date(e.dateTime).getTime(), v: e.rating, votes: e.votesCount });
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.t - b.t));
    return m;
  }, [history]);

  return (
    <>
      <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setOpen(true)}>
        View rating history
      </Button>
      <Modal opened={open} onClose={() => setOpen(false)} title="Rating history" size="lg" lockScroll={false}>
        {loading && (
          <div className="flex justify-center py-4">
            <Loader size="sm" />
          </div>
        )}
        {error && <Text c="red" size="sm">{String(error)}</Text>}
        {!loading && !error && loaded && Object.keys(byPlatform).length === 0 && (
          <Text size="sm" c="dimmed">No rating history recorded for this art yet.</Text>
        )}
        {!loading && !error && Object.keys(byPlatform).length > 0 && (
          <div className="flex flex-col">
            {Object.entries(byPlatform).map(([platform, points]) => {
              const meta = PLATFORM_META[platform] || { label: platform, icon: null };
              return <RatingChart key={platform} points={points} label={meta.label} icon={meta.icon} />;
            })}
          </div>
        )}
      </Modal>
    </>
  );
};

export default AdminRatingHistoryButton;
